import { apiGet, apiPost, apiPatch, apiProjectsGet, apiProjectsPost, apiProjectsPatch, apiProjectsDelete, getFreshToken } from '../../utilities/apiClient';
import { generateMnemonic } from '../../utilities/generateMnemonic';
import { getSessionIds, getStoreState } from './_sessionIds';
import config from '../../config';
import debug from '../../utilities/debug';
import {
  ADD_PROJECTS,
  ADD_PROJECT,
  ADD_QUERIES,
  ADD_CONVERSATION,
  FETCHING_PROJECTS,
  FETCHED_PROJECTS,
  FETCHING_QUERIES,
  FETCHED_QUERIES,
  UPDATE_PROJECT,
  REMOVE_PROJECT,
} from '../constants';


export const markSOSSeen = (
  projectId: string | number,
  eventId: string,
) => async (): Promise<{ success: boolean; payload?: { seen_by: Array<{ cognitoId: string; name: string; seenAt: string }> } }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPost(`/emergency-sos-ack/${projectId}/${accountId}/${cognitoId}/${eventId}`,
      {},
    );
    return response;
  } catch {
    return { success: false };
  }
};

export const fetchProjects = (accountId: string, cognitoId: string) => async (dispatch: any) => {
  dispatch({ type: FETCHING_PROJECTS });
  try {
    const response = await apiProjectsGet(`/get-all/${accountId}/${cognitoId}`);
    if (response?.success) {
      dispatch({ type: ADD_PROJECTS, payload: response.payload || [] });
      dispatch({ type: FETCHED_PROJECTS });
      return { success: true, payload: response.payload };
    }
    dispatch({ type: FETCHED_PROJECTS });
    return { success: false };
  } catch (err: any) {
    dispatch({ type: FETCHED_PROJECTS });
    return { success: false, message: err?.message };
  }
};

export const fetchQueries = (projectId: number | string, override = false) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  const pid = String(projectId);

  const state = getStoreState();
  const already = state.prompts?.queries?.fetched?.[pid];
  if (already && !override) return { success: true };

  dispatch({ type: FETCHING_QUERIES });
  try {
    const response = await apiGet(`/prompts/get-queries/${pid}/${accountId}/${cognitoId}`);
    if (response?.success) {
      dispatch({ type: ADD_QUERIES, payload: { project_id: pid, queries: response.payload || [] } });
      dispatch({ type: FETCHED_QUERIES });
      return { success: true, payload: response.payload };
    }
    dispatch({ type: FETCHED_QUERIES });
    return { success: false };
  } catch (err: any) {
    dispatch({ type: FETCHED_QUERIES });
    return { success: false, message: err?.message };
  }
};

export const fetchConversation = (conversationId: string, projectId: number | string) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiGet(`/prompts/get-conversation/${conversationId}/${accountId}/${cognitoId}`);
    if (response?.success && response.payload) {
      dispatch({
        type: ADD_CONVERSATION,
        payload: { project_id: String(projectId), queries: response.payload },
      });
      return { success: true, payload: response.payload };
    }
    return { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const fetchQueriesForConversation = fetchConversation;

// ─── Update Query (rating, etc.) ─────────────────────────────────────────────

export const updateQuery = (
  projectId: number | string,
  queryId: number | string,
  updates: Record<string, any>,
) => async (_dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiPatch(
      `/prompts/update-query/${projectId}/${queryId}/${accountId}/${cognitoId}`,
      { updates },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Update Project ───────────────────────────────────────────────────────────

export const updateProject = (
  projectId: number | string,
  updates: Record<string, any>,
) => async (dispatch: any, getState: () => any) => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPatch(`/update/${projectId}/${accountId}/${cognitoId}`,
      { updates },
    );
    if (response?.success && response.payload) {
      // The raw DB response has `users` as a plain array of cognito ID strings.
      // The projects list endpoint enriches `users` via a JOIN (adding cognito_id,
      // project_permissions, etc.). Preserve the existing enriched users so that
      // permission checks (e.g. watch carousel) continue to work after an update.
      const existingProject = getState()?.projects?.list?.[String(projectId)];
      const enrichedUsers = existingProject?.users;
      const payload = enrichedUsers
        ? { ...response.payload, users: enrichedUsers }
        : response.payload;
      dispatch({ type: ADD_PROJECT, payload });
    }
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Remove Project ──────────────────────────────────────────────────────────

export const removeProject = (
  projectId: number | string,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const response = await apiProjectsDelete(
      `/delete/${projectId}/${accountId}/${cognitoId}`,
    );
    if (response?.success) {
      dispatch({ type: REMOVE_PROJECT, payload: projectId });
    }
    return response ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not delete this plan.' };
  }
};

/**
 * Fetch the project's past inbox addresses (distinct prior mnemonics
 * across `versioned` rows). Mail to those addresses still routes to the
 * project — this endpoint exists so the UI can show a caregiver which
 * old address they may have handed out before changing it.
 *
 * Returns an array of `{ mnemonic, valid_until }`, newest-first. The
 * currently-active mnemonic is excluded server-side. Not cached in
 * Redux — callers (one screen each, low-volume) keep this in local
 * state and refetch on focus.
 */
export interface InboxAlias {
  mnemonic: string;
  valid_until: string | null;
}
export const fetchProjectInboxAliases = async (
  projectId: number | string,
): Promise<InboxAlias[]> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return [];
  try {
    const response = await apiProjectsGet(
      `/${projectId}/${accountId}/${cognitoId}/inbox-aliases`,
    );
    if (response?.success && Array.isArray(response.payload)) {
      return response.payload as InboxAlias[];
    }
    return [];
  } catch {
    return [];
  }
};

/**
 * Enable (or disable) survey check-in questions for a project.
 *
 * 1. PATCHes the project blob with the provided settings.
 * 2. If enabling (method !== 'none'), calls the schedule-questions endpoint
 *    to kick off the self-managing question chain immediately.
 */
export const enableProjectQuestions = (
  projectId: number | string,
  settings: {
    prompts_method: 'sms' | 'push' | 'email' | 'none';
    prompts_frequency?: string;
    prompts_time?: string;
    adaptive_cadence?: boolean;
    remind?: boolean;
    prompts_chat_enabled?: boolean;
  },
) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();

  // 1 — Persist the settings to the project blob
  const updateResp = await dispatch(updateProject(projectId, { blob: settings }));
  if (!updateResp?.success) {
    return updateResp;
  }

  // 2 — Start (or cancel) the question chain
  try {
    const response = await apiProjectsPost(`/schedule-questions/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Create Project ───────────────────────────────────────────────────────────

export const createProject = (project: any) => async (dispatch: any) => {
  const { accountId, cognitoId } = getSessionIds();
  const projectWithMnemonic = project.mnemonic ? project : { ...project, mnemonic: generateMnemonic() };
  try {
    const response = await apiProjectsPost(`/create/${accountId}/${cognitoId}`, { project: projectWithMnemonic });
    if (response?.success) {
      dispatch({ type: ADD_PROJECT, payload: response.payload });
      return { success: true, payload: response.payload };
    }
    return { success: false, message: response?.message || 'Could not create project.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not create project.' };
  }
};

// ─── Extract from text (Typeform Step 0 bulk-fill) ──────────────────────────
// One-shot AI extraction: free-form text in, partial field map + ready-to-
// save medication / contact arrays out. Used by the project-creation
// Typeform's "describe in your own words" affordance to pre-fill the form
// AND seed the new project with mentioned medications/contacts.
//
// Returns `{ success, extracted, medications, contacts, rationale, message? }`.
// All four data fields are always present:
//   - `extracted`:   object (possibly empty)
//   - `medications`: array  (possibly empty)
//   - `contacts`:    array  (possibly empty)
//   - `rationale`:   string (possibly empty)
// Caller merges `extracted` into survey-core state, surfaces `medications`
// and `contacts` in the review screen, and after createProject succeeds
// fans out `/medications/create` and `/contacts/create` POSTs for any rows
// the user kept.
export interface ExtractedMedicationShape {
  name: string;
  dose?: string;
  unit?: string;
  frequency?: string;
  physician?: string;
  notes?: string;
}
export interface ExtractedContactShape {
  first_name: string;
  last_name?: string;
  type?: string;
  email?: string;
  phone?: string;
  notes?: string;
}
export interface ExtractFromTextResult {
  success: boolean;
  extracted: Record<string, any>;
  medications: ExtractedMedicationShape[];
  contacts: ExtractedContactShape[];
  rationale: string;
  message?: string;
}
export async function extractPlanFieldsFromText(
  freeText: string,
  mode: 'client' | 'partner' = 'client',
): Promise<ExtractFromTextResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, extracted: {}, medications: [], contacts: [], rationale: '', message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/extract-from-text/${accountId}/${cognitoId}`,
      { free_text: freeText, mode },
    );
    if (response?.success && response.payload) {
      return {
        success: true,
        extracted: (response.payload.extracted && typeof response.payload.extracted === 'object')
          ? response.payload.extracted
          : {},
        medications: Array.isArray(response.payload.medications) ? response.payload.medications : [],
        contacts: Array.isArray(response.payload.contacts) ? response.payload.contacts : [],
        rationale: typeof response.payload.rationale === 'string' ? response.payload.rationale : '',
      };
    }
    return {
      success: false,
      extracted: {},
      medications: [],
      contacts: [],
      rationale: '',
      message: response?.message || 'Extraction failed.',
    };
  } catch (err: any) {
    return {
      success: false,
      extracted: {},
      medications: [],
      contacts: [],
      rationale: '',
      message: err?.message || 'Network error.',
    };
  }
}

// ─── Document-driven intake primitives ──────────────────────────────────────
//
// Three thin wrappers around the backend endpoints introduced in PR #853.
// Used by the Typeform "Upload a document" flow:
//
//   1. startIntake()                       → creates an intake_pending project
//   2. (existing) uploadDocument(...)      → file goes through standard processor
//   3. extractSurveyFieldsFromProject(pid) → post-process, pull survey fields
//   4. finalizeIntake(pid, project)        → flip status='active', patch fields
//
// The intake_pending row is invisible to /get-all and every other read until
// finalize flips it, so an abandoned mid-flow project doesn't surface
// anywhere user-visible.

export interface StartIntakeResult {
  success: boolean;
  project_id?: number | string | null;
  message?: string;
  errorCode?: string;
  limit?: number;
  current?: number;
  /** ED-2563 advisory: drafting is always allowed; at_cap means activation
   *  (finalize) will refuse until the user upgrades or frees a slot. */
  cap?: { at_cap: boolean; limit: number | null; current: number };
}
export async function startIntake(): Promise<StartIntakeResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/start-intake/${accountId}/${cognitoId}`,
      {},
    );
    if (response?.success && response.payload?.project_id != null) {
      return { success: true, project_id: response.payload.project_id, cap: response.payload.cap };
    }
    return {
      success: false,
      message: response?.message || 'Could not start intake.',
      errorCode: response?.errorCode,
      limit: response?.limit,
      current: response?.current,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

export interface ExtractSurveyFieldsFromProjectResult {
  success: boolean;
  extracted: Record<string, any>;
  rationale: string;
  message?: string;
}
export async function extractSurveyFieldsFromProject(
  projectId: number | string,
): Promise<ExtractSurveyFieldsFromProjectResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, extracted: {}, rationale: '', message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/extract-survey-fields-from-project/${projectId}/${accountId}/${cognitoId}`,
      {},
    );
    if (response?.success && response.payload) {
      return {
        success: true,
        extracted: (response.payload.extracted && typeof response.payload.extracted === 'object')
          ? response.payload.extracted
          : {},
        rationale: typeof response.payload.rationale === 'string' ? response.payload.rationale : '',
      };
    }
    return {
      success: false,
      extracted: {},
      rationale: '',
      message: response?.message || 'Could not extract survey fields.',
    };
  } catch (err: any) {
    return {
      success: false,
      extracted: {},
      rationale: '',
      message: err?.message || 'Network error.',
    };
  }
}

// List the caller's outstanding intake_pending projects. Powers the
// Plans-screen resume cards. Each row carries the cached extraction +
// in-progress walker answers, plus the document and per-table counts
// the card needs to preview.
export interface IntakePendingRow {
  project_id: number | string;
  created_at: string;
  blob: {
    intake?: boolean;
    intake_extracted?: { extracted?: Record<string, any>; rationale?: string; extracted_at?: string };
    intake_progress?: { data?: Record<string, any>; updated_at?: string };
  };
  document: { id: number | string; filename: string; status: string; summary?: string | null } | null;
  medication_count: number;
  contact_count: number;
}
export interface FetchIntakePendingResult {
  success: boolean;
  rows: IntakePendingRow[];
  message?: string;
}
export async function fetchIntakePending(): Promise<FetchIntakePendingResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, rows: [], message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsGet(
      `/intake-pending/${accountId}/${cognitoId}`,
    );
    if (response?.success && Array.isArray(response.payload)) {
      return { success: true, rows: response.payload };
    }
    return { success: false, rows: [], message: response?.message || 'Could not list pending intakes.' };
  } catch (err: any) {
    return { success: false, rows: [], message: err?.message || 'Network error.' };
  }
}

// Persist partial walker answers on the intake row. Called debounced from
// the renderer after each answer commit. Failures are silently logged —
// the next save overwrites. We don't surface errors to the user because
// the typical failure mode is transient network and they're in the middle
// of a flow.
export async function saveIntakeProgress(
  projectId: number | string,
  data: Record<string, any>,
): Promise<{ success: boolean; message?: string }> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/save-intake-progress/${projectId}/${accountId}/${cognitoId}`,
      { data },
    );
    if (response?.success) return { success: true };
    return { success: false, message: response?.message || 'Save failed.' };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

export interface FinalizeIntakeResult {
  success: boolean;
  payload?: any;
  message?: string;
  /** PLANS_LIMIT_REACHED when activation was refused at the cap (ED-2563) —
   *  the draft shell is untouched and resumable. */
  errorCode?: string;
  limit?: number;
  current?: number;
}
export async function finalizeIntake(
  projectId: number | string,
  project: any,
): Promise<FinalizeIntakeResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsPost(
      `/finalize-intake/${projectId}/${accountId}/${cognitoId}`,
      { project },
    );
    if (response?.success && response.payload) {
      return { success: true, payload: response.payload };
    }
    return {
      success: false,
      message: response?.message || 'Could not finalize intake.',
      errorCode: response?.errorCode,
      limit: response?.limit,
      current: response?.current,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Network error.' };
  }
}

// ─── Survey configuration fetch (no version filter) ─────────────────────────
//
// Fetches a SurveyJS configuration by `survey_id` via the existing
// `/projects/get-json/{survey_id}/...` endpoint — which is unversioned by
// survey_id and DynamoDB-cached. The Typeform renderer needs a survey-by-id
// fetch that doesn't apply the global block_version filter (the project-
// creation survey is pinned to v1; the redux `state.blocks.list` slice is
// populated by `/blocks/get-all` which IS version-filtered, so a screen
// reading from it can see an empty list).
export interface FetchSurveyConfigResult {
  success: boolean;
  configuration: any | null;
  message?: string;
}
export async function fetchSurveyConfig(
  surveyId: string,
): Promise<FetchSurveyConfigResult> {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) {
    return { success: false, configuration: null, message: 'Not signed in.' };
  }
  try {
    const response = await apiProjectsGet(
      `/get-json/${surveyId}/${accountId}/${cognitoId}`,
    );
    if (response?.success && response.payload?.configuration) {
      return { success: true, configuration: response.payload.configuration };
    }
    return {
      success: false,
      configuration: null,
      message: response?.message || 'Survey not found.',
    };
  } catch (err: any) {
    return {
      success: false,
      configuration: null,
      message: err?.message || 'Network error.',
    };
  }
}

// ─── Project Creation Chat (streaming) ───────────────────────────────────────

export interface ProjectCreationStreamCallbacks {
  onDelta: (token: string) => void;
  onData: (extracted: Record<string, any>, isComplete: boolean, schema?: Record<string, any> | null, fieldOrder?: string[]) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

export const streamProjectCreationChat = (
  message: string,
  collectedData: Record<string, any>,
  conversationHistory: Array<{ role: string; content: string }>,
  callbacks: ProjectCreationStreamCallbacks,
  options: { mode?: string; survey_id?: string | null; isKickoff?: boolean } = {},
): void => {
  // Wrap the whole prelude in an async IIFE so we can `await getFreshToken()`
  // before opening the XHR. The Redux session.token can be stale (especially
  // on long-lived web/desktop tabs that survive a Cognito ID-token refresh),
  // and the create-chat lambda only sees the Bearer header we send here —
  // there's no API-Gateway authorizer to silently refresh it for us. Without
  // this, a stale token round-trips as `{type:'error', message:'Unauthorized'}`
  // and the user gets stuck on the project-onboarding chat (ED-2078).
  void (async () => {
  const state = getStoreState();
  const accountId = state?.session?.account?.account_id;
  const cognitoId = state?.authentication?.user?.cognito_id;
  let token = state?.session?.token || '';
  try {
    const fresh = await getFreshToken();
    if (fresh) token = fresh;
  } catch {
    // Network blip during refresh — fall back to the redux token. If it's
    // still valid we proceed; if not, the backend will reject and onError
    // surfaces the failure normally.
  }
  const baseUrl = config.streaming.createChatUrl;

  if (!baseUrl) {
    debug.error('streamProjectCreationChat: createChatUrl missing — set EXPO_PUBLIC_CREATE_CHAT_URL');
    callbacks.onError('Project creation chat endpoint not configured');
    return;
  }
  // The backend rejects requests with a missing account_id / token by
  // emitting a single `{type:'error'}` frame and ending the stream — but
  // historically the front-end ignored those frames, leaving the assistant
  // bubble pulsing forever with no surfaced error. Bail early when we
  // already know the request can't succeed so the screen gets a real
  // failure to render.
  if (!accountId) {
    debug.error('streamProjectCreationChat: account_id missing on session — refusing to send');
    callbacks.onError('No account on session. Sign out and back in.');
    return;
  }
  if (!cognitoId) {
    debug.error('streamProjectCreationChat: cognito_id missing on session — refusing to send');
    callbacks.onError('No user on session. Sign out and back in.');
    return;
  }
  if (!token) {
    debug.error('streamProjectCreationChat: session token missing — refusing to send');
    callbacks.onError('Session expired. Sign out and back in.');
    return;
  }

  const url = `${baseUrl}/${accountId}/${cognitoId}`;
  debug.log('chat', 'streamProjectCreationChat → POST', { url, messageLen: message.length });

  let lineBuffer = '';
  let processedLength = 0;
  // Backend-emitted `{type:'error', message}` frames terminate the stream
  // early. We must call onError exactly once and suppress the subsequent
  // onDone that fires when xhr.onload runs on the (gracefully closed) 200
  // response — otherwise the screen would clear the bubble (onError) and
  // then the next render path would re-mark it streaming=false (onDone)
  // and push an empty bubble back into view.
  let terminated = false;
  const fail = (reason: string) => {
    if (terminated) return;
    terminated = true;
    debug.warn('streamProjectCreationChat failed', { reason });
    callbacks.onError(reason);
  };

  const processChunk = (newText: string) => {
    lineBuffer += newText;
    const parts = lineBuffer.split('\n');
    lineBuffer = parts.pop() ?? '';

    for (const line of parts) {
      // Support both SSE (`data: {...}`) and plain NDJSON (`{...}`)
      const raw = line.startsWith('data: ') ? line.slice(6) : line.trim();
      if (!raw || raw === '[DONE]') continue;

      let parsed: any;
      try { parsed = JSON.parse(raw); } catch { continue; }

      // Server-emitted error frame — surface to the UI and stop processing.
      if (parsed.type === 'error') {
        fail(parsed.message || 'Server returned an error.');
        continue;
      }

      // Content delta — server may send `content` or `type:'delta'` with `data`
      const delta = parsed.content ?? (parsed.type === 'delta' ? parsed.data : null);
      if (typeof delta === 'string' && delta.length) {
        callbacks.onDelta(delta);
      }

      // Extracted data event
      if (parsed.type === 'data') {
        callbacks.onData(
          parsed.extracted || {},
          parsed.is_complete || false,
          parsed.field_schema || null,
          parsed.field_order || [],
        );
      }
    }
  };

  const xhr = new XMLHttpRequest();
  xhr.open('POST', url);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('Accept', 'text/event-stream');
  xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  // Hard ceiling so a stuck lambda (cold start, downstream LLM hang, etc.)
  // can't leave the chat bubble pulsing forever without surfacing anything.
  // Backed off generously because cold-starts + the LLM round-trip can
  // legitimately take 20–30s on the first turn of the conversation.
  xhr.timeout = 60_000;

  xhr.onprogress = () => {
    if (terminated) return;
    const newText = xhr.responseText.slice(processedLength);
    processedLength = xhr.responseText.length;
    if (newText) processChunk(newText);
  };

  xhr.onload = () => {
    if (terminated) return;
    const remaining = xhr.responseText.slice(processedLength);
    if (remaining) processChunk(remaining);
    if (terminated) return;
    if (lineBuffer.trim()) {
      try {
        const parsed = JSON.parse(lineBuffer);
        if (parsed.type === 'error') {
          fail(parsed.message || 'Server returned an error.');
          return;
        }
        const delta = parsed.content ?? (parsed.type === 'delta' ? parsed.data : null);
        if (typeof delta === 'string' && delta.length) callbacks.onDelta(delta);
        if (parsed.type === 'data') {
          callbacks.onData(parsed.extracted || {}, parsed.is_complete || false, parsed.field_schema || null, parsed.field_order || []);
        }
      } catch { /* incomplete fragment */ }
    }
    if (xhr.status < 200 || xhr.status >= 300) {
      fail(`HTTP ${xhr.status}${xhr.responseText ? `: ${xhr.responseText.slice(0, 200)}` : ''}`);
      return;
    }
    callbacks.onDone();
  };

  xhr.onerror = () => fail('Network error');
  xhr.ontimeout = () => fail('Request timed out');

  xhr.send(JSON.stringify({
    account_id: accountId,
    message,
    collected_data: collectedData,
    conversation_history: conversationHistory,
    mode: options.mode || 'client',
    survey_id: options.survey_id || null,
    is_kickoff: !!options.isKickoff,
  }));
  })();
};

/**
 * Optimistically updates a project's health_alerts_config in Redux, then
 * patches the backend. Rolls back on failure.
 */
export const updateHealthAlertsConfig = (
  projectId: string | number,
  config: Record<string, any>,
) => async (dispatch: any): Promise<{ success: boolean; message?: string; payload?: { health_alerts_config: any } }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false };

  // Optimistic update.
  dispatch({ type: UPDATE_PROJECT, payload: { id: projectId, health_alerts_config: config } });

  try {
    const response = await apiProjectsPatch(`/update-health-alerts-config/${projectId}/${accountId}/${cognitoId}`,
      { config },
    );
    if (response?.success) {
      // Sync with server-confirmed config.
      dispatch({
        type: UPDATE_PROJECT,
        payload: { id: projectId, health_alerts_config: response.payload?.health_alerts_config },
      });
      return { success: true, payload: response.payload };
    }
    // Roll back optimistic update.
    const state = getStoreState() as any;
    const current = state.projects?.list?.[projectId]?.health_alerts_config;
    dispatch({ type: UPDATE_PROJECT, payload: { id: projectId, health_alerts_config: current } });
    return { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Diagnoses Catalog ────────────────────────────────────────────────────────
//
// Fetches the active diagnoses catalog rows and submits user-generated requests
// for new diagnoses. Server contract lives under /diagnoses/* (global catalog,
// not per-project). See backend/api/client/controllers/projects/list-diagnoses
// and request-diagnosis — named under `projects/` in the controller tree for
// domain grouping, but exposed at top-level paths to avoid colliding with the
// greedy /projects/{project_id}/... route.

export interface DiagnosisCatalogRow {
  id: number;
  code: string;
  label: string;
  category: string | null;
  synonyms: string[] | null;
  status: 'active' | 'archived';
  metadata?: Record<string, any> | null;
  updated_at?: string | null;
  created_at?: string | null;
}

export const listDiagnosesCatalog = async (): Promise<{
  success: boolean;
  payload?: DiagnosisCatalogRow[];
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiGet(`/diagnoses/list/${accountId}/${cognitoId}`);
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

export const requestDiagnosis = async (
  label: string,
  projectId?: number | string | null,
): Promise<{
  success: boolean;
  deduped?: boolean;
  payload?: any;
  message?: string;
}> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiPost(
      `/diagnoses/request/${accountId}/${cognitoId}`,
      {
        label,
        project_id: projectId == null ? null : projectId,
      },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Transfer project ─────────────────────────────────────────────────────────
//
// Hand a plan over to another Eleplan account. The recipient must:
//   1. have an active Eleplan account,
//   2. not be a partner account,
//   3. supply the initiator with the plan's 6-digit `transfer_code`.
//
// Reuses the same endpoint that the web app calls (see
// backend/api/client/controllers/projects/transfer). The caller must have
// `owner` or `administrator` on the current account — the backend re-checks.
//
// On success the projects list is refetched so the transferred plan disappears
// locally; the backend has already inactivated the initiator's membership.

export const transferProject = (
  email: string,
  projectId: number | string,
  transferCode: string,
) => async (dispatch: any): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  try {
    const response = await apiProjectsPost(`/transfer/${accountId}/${cognitoId}`,
      {
        email: String(email || '').trim().toLowerCase(),
        project_id: projectId,
        transfer_code: String(transferCode || '').trim(),
      },
    );
    if (response?.success) {
      // Refresh the projects list so the transferred plan is removed and any
      // pending-transfer flags show on membership rows.
      try { await dispatch(fetchProjects(accountId, cognitoId)); } catch {}
    }
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message };
  }
};

// ─── Project memberships ─────────────────────────────────────────────────────
//
// Add or remove team members on a project. Backed by the projects service's
// /create-membership and /delete-membership endpoints. Mirrors the legacy
// frontend/client signatures (memberships, project_id, full) and
// (membership_ids, project_id) so callers can drop them in unchanged.
//
// Cache invalidation is intentionally out of scope here — the partner screen
// callers (PartnerCustomerDetailScreen → ManageMembersSheet) own their own
// `onMembersChanged` / `onAfterChange` refresh hooks. Wiring fetchProjects /
// account-wide refetches in here would double-fetch on every save.

export interface MembershipCreateRow {
  project_id: number | string;
  account_id: string | number;
  cognito_id: string;
  // Optional permissions/role passthrough — backend tolerates unknown fields.
  [key: string]: any;
}

export interface MembershipDeleteRow {
  id: number | string;
  cognito_id: string;
  account_id: string | number;
}

export const createProjectMembership = (
  memberships: MembershipCreateRow[],
  _projectId?: number | string,
  full = false,
  // The account that OWNS the target project. For a partner assigning a
  // caseworker to a CUSTOMER's plan, the project lives on the customer's
  // account — create-membership must target that account, not the partner's
  // session account, or the backend's `WHERE project.account_id = <path>`
  // ownership check drops every row → "Could not create project memberships".
  accountIdOverride?: string,
) => async (): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId: sessionAccountId, cognitoId } = getSessionIds();
  const accountId = accountIdOverride || sessionAccountId;
  try {
    const response = await apiProjectsPost(
      `/create-membership/${accountId}/${cognitoId}`,
      { memberships, full },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not add team members.' };
  }
};

/**
 * Update the current user's own project_membership row on a single project.
 * Used for self-toggleable flags like email_forwarding_enabled. Hits the
 * existing update-account-user controller with target_cognito_id = self —
 * the controller already authorizes acting-as-self because it only checks
 * that the target is on the same account.
 */
export const updateOwnProjectMembership = (
  projectId: number | string,
  updates: Record<string, any>,
) => async (): Promise<{ success: boolean; message?: string }> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  try {
    const response = await apiPatch(
      `/users/update-account-user/${projectId}/${cognitoId}/${accountId}/${cognitoId}`,
      { updates: { project_membership: updates } },
    );
    return response ?? { success: false };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Update failed' };
  }
};

/**
 * Ask the plan's admins for permissions you don't have.
 *
 * The server records the ask on your own project_membership row and pushes
 * everyone who could approve it — account owners/administrators plus the
 * plan's `members` holders. Nothing is granted here; an admin still has to
 * approve, which is why this is safe to expose to every member.
 */
export const requestProjectPermissions = (
  projectId: number | string,
  permissions: string[],
  accountPermissions: string[] = [],
) => async (): Promise<{
  success: boolean; message?: string; requested?: string[]; accountRequested?: string[];
}> => {
  const { accountId, cognitoId } = getSessionIds();
  if (!accountId || !cognitoId) return { success: false, message: 'Not authenticated' };
  if (!permissions?.length && !accountPermissions?.length) return { success: false, message: 'Nothing selected' };
  try {
    const response = await apiPost(
      // `projectId` may be undefined for an account-only ask; the server
      // requires a real one only when plan permissions are in the payload.
      `/users/request-permissions/${projectId}/${accountId}/${cognitoId}`,
      { permissions, account_permissions: accountPermissions },
    );
    return {
      success: response?.success !== false,
      message: response?.message,
      requested: response?.payload?.requested_permissions,
      accountRequested: response?.payload?.account_requested_permissions,
    };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not send your request.' };
  }
};

export const deleteProjectMembership = (
  membershipIds: MembershipDeleteRow[],
  projectId: number | string,
  // Same as createProjectMembership: pass the CUSTOMER's account when a partner
  // is managing a caseworker on a customer's plan, so the path targets the
  // account that owns the project rather than the partner's session account.
  accountIdOverride?: string,
) => async (): Promise<{ success: boolean; message?: string; payload?: any }> => {
  const { accountId: sessionAccountId, cognitoId } = getSessionIds();
  const accountId = accountIdOverride || sessionAccountId;
  try {
    const response = await apiProjectsDelete(
      `/delete-membership/${projectId}/${accountId}/${cognitoId}`,
      { membership_ids: membershipIds },
    );
    return response;
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not remove team members.' };
  }
};
